import React, { ChangeEvent } from 'react';
import {
  Box,
  FormControl,
  FormGroup,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { isString } from 'lodash';

import LabelCheckbox from '../LabelCheckbox';
import LabelRadio from '../LabelRadio';

import { OptionType, QuestionGroupProps } from './props';

const QuestionGroup = ({
  selectionList = [],
  type,
  value = [],
  onChange,
  spacing = type === 'multiple' ? 0 : 2,
  columns = 3,
  mobileColumns = 1,
  labelCheckboxProps,
  labelRadioProps,
  itemsContainerProps,
  labelProps,
  customOptionButton: CustomOptionButton,
}: QuestionGroupProps) => {
  //======================= HOOKS
  const theme = useTheme();

  //======================= EVENTS
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;
    if (type === 'multiple') {
      const checkboxValues = Array.isArray(value) ? [...value] : [];
      const index = checkboxValues.indexOf(selectedValue);
      if (index === -1) {
        checkboxValues.push(selectedValue);
      } else {
        checkboxValues.splice(index, 1);
      }
      onChange(checkboxValues);
    } else {
      onChange(selectedValue);
    }
  };

  // ======================= VIEWS
  const renderLabel = ({
    label,
    selected,
  }: {
    label: any;
    selected: boolean;
  }) => {
    if (isString(label)) {
      return (
        <Typography
          variant={'body2'}
          color={selected ? 'primary' : 'secondary'}
          {...labelProps}
        >
          {label}
        </Typography>
      );
    }
    return React.isValidElement(label)
      ? React.cloneElement<any>(label, { selected: selected })
      : label;
  };
  const renderMultipleOptions = (option) => {
    if (CustomOptionButton) {
      return (
        <CustomOptionButton
          key={option.value}
          value={option.value}
          label={renderLabel({
            label: option.label,
            selected: value.includes(option.value),
          })}
          checked={value.includes(option.value)}
          onChange={handleChange}
          {...labelCheckboxProps}
        />
      );
    }
    return (
      <LabelCheckbox
        sx={{
          margin: 0,
        }}
        key={option.value}
        value={option.value}
        label={renderLabel({
          label: option.label,
          selected: value.includes(option.value),
        })}
        checkboxProps={{
          checked: value.includes(option.value),
          onChange: handleChange,
        }}
        {...labelCheckboxProps}
      />
    );
  };
  const renderSingleOptions = (option) => {
    if (CustomOptionButton) {
      return (
        <CustomOptionButton
          key={option.value}
          value={option.value}
          label={renderLabel({
            label: option.label,
            selected: value === option.value,
          })}
          checked={value === option.value}
          {...labelCheckboxProps}
        />
      );
    }
    return (
      <LabelRadio
        sx={{
          border: 2,
          borderColor:
            option.value === value
              ? theme.palette.primary.main
              : theme.palette.divider,
          borderRadius: '8px',
          height: '100%',
          alignItems: 'center',
          margin: 0,
          width: '100%',
        }}
        key={option.value}
        value={option.value}
        label={renderLabel({
          label: option.label,
          selected: option.value === value,
        })}
        {...labelRadioProps}
      />
    );
  };
  const renderItem = () => {
    if (type === 'multiple') {
      return (
        <FormGroup sx={{ display: 'grid' }}>
          <Box
            display="grid"
            className="grid-cols-6 md:grid-cols-12"
            gap={spacing}
          >
            {selectionList.map((option: OptionType, i: number) => (
              <Box
                key={i}
                gridColumn={{
                  xs: `span ${12 / mobileColumns}`,
                  md: `span ${12 / columns}`,
                }}
                {...itemsContainerProps}
              >
                {renderMultipleOptions(option)}
              </Box>
            ))}
          </Box>
        </FormGroup>
      );
    }
    return (
      <RadioGroup
        name="option"
        value={value as string}
        onChange={handleChange}
        sx={{ display: 'grid' }}
      >
        <Box
          display="grid"
          className="grid-cols-6 md:grid-cols-12"
          gap={spacing}
        >
          {selectionList.map((option: OptionType, i: number) => (
            <Box
              key={i}
              gridColumn={{
                xs: `span ${12 / mobileColumns}`,
                md: `span ${12 / columns}`,
              }}
              {...itemsContainerProps}
            >
              {renderSingleOptions(option)}
            </Box>
          ))}
        </Box>
      </RadioGroup>
    );
  };

  return (
    <FormControl fullWidth component="fieldset">
      {renderItem()}
    </FormControl>
  );
};
export default QuestionGroup;
